import { downloadSubmittalCsv } from '@/services/submittalService'

/**
 * Fetches a submittal as CSV and hands it to the browser as a download.
 *
 * Shared by both listings' row action and the detail page's header button, which
 * each had their own copy of the blob-and-anchor dance. Loading state and toasts
 * stay with the caller, since each shows them differently.
 *
 * Resolves `{ error }` rather than throwing: every caller reports the failure in
 * its own UI instead of letting it escape.
 *
 * `fallbackName` names the file only when the response carried no
 * Content-Disposition. The server's own name wins, as it does in pitstop, so a
 * submittal downloaded from either app arrives named the same.
 *
 * Worth knowing which name that is: the service sends `{data_element_id}.csv`,
 * so the listings' fallback is already identical to it, while the detail page's
 * — the element's display name — is the more readable of the two and is now
 * superseded. That is the cost of having one side own the name; the fix belongs
 * in the handler that builds it.
 *
 * The fallback itself is deliberately not pitstop's `submittal_{id}.csv`. It
 * only applies where a deployment does not expose the header, and a caller's
 * name — the element's, or its id — tells the reader which submittal they have
 * in a way a bare UUID does not.
 */
export const downloadSubmittalAsCsv = async (submittalId, fallbackName) => {
  const { data, fileName, error } = await downloadSubmittalCsv(submittalId, 'csv')
  if (error) return { error }

  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName || `${fallbackName}.csv`
  // In the document before it is clicked, as pitstop does. Chromium honours a
  // click on a detached anchor; Firefox and Safari can treat it as a no-op, and
  // the failure is silent — no file, and nothing thrown for the caller to
  // report, so Download reads as a dead button.
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)

  return { error: null }
}

export default downloadSubmittalAsCsv
